import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { MonthlyExpenseBranchRow } from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import { FOOT_ROW_SX } from "../DailyExpense/tableStyles";
import { CELL_SX, ESTIMATE_TEXT_SX, GROUP_SEPARATOR_SX, pinnedSx } from "./closeTableStyles";
import { EMPTY_TEXT } from "./constants";
import {
  BranchFigures,
  CloseDraft,
  ESTIMATE_SIDES,
  dailyCellKeyOf,
  extrasTotalOf,
  routeDaysOf,
} from "./monthlyExpenseDraft";
import {
  CloseColumn,
  columnKeyOf,
  formatSignedAmount,
  isGroupStart,
} from "./monthlyExpenseLayout";
import { AmountView, sumMoney, viewTotalOf, weightedAverageOf } from "./monthlyExpenseMath";

const FOOTER_CORNER_LAYER = 4;

interface CloseTableFootProps {
  rows: MonthlyExpenseBranchRow[];
  draft: CloseDraft;
  figures: BranchFigures[];
  columns: CloseColumn[];
  view: AmountView;
}

const formatOrEmpty = (value: number | null) => (value === null ? EMPTY_TEXT : formatNumber(value));

/** วันขาย is shown only when every branch ran the same number of days. */
const commonSaleDaysOf = (rows: MonthlyExpenseBranchRow[]) =>
  rows.every((row) => row.sale_days === rows[0]?.sale_days) ? rows[0]?.sale_days : undefined;

/**
 * The sticky รวม row: each column over every branch in the current view, and under Sales /
 * Manager the company average per route per day and the monthly impact of the adjustments
 * (difference × วันขาย × สาย, added up).
 */
export default function CloseTableFoot({ rows, draft, figures, columns, view }: Readonly<CloseTableFootProps>) {
  const amountOf = (row: MonthlyExpenseBranchRow, index: number, column: CloseColumn): number => {
    const branch = draft.branches[row.branch_code];
    if (column.kind === "DAILY") return branch.daily[dailyCellKeyOf(column.categoryCode, column.group)] ?? 0;
    if (column.kind === "ITEM") return branch.items[column.categoryCode] ?? 0;
    if (column.kind === "EXTRA") return extrasTotalOf(branch, column.groupKey);
    return figures[index].groupTotals[column.groupKey];
  };
  const columnTotal = (column: CloseColumn) =>
    viewTotalOf(
      rows.map((row, index) => ({
        amount: amountOf(row, index, column),
        saleDays: row.sale_days,
        routeCount: row.route_count,
      })),
      view,
    );
  const estimateTotals = ESTIMATE_SIDES.map((side) => {
    const estimates = figures.map((figure) => figure.estimate[side]);
    return {
      side,
      average: weightedAverageOf(
        estimates.map((estimate, index) => ({
          value: estimate.effective,
          routeDays: routeDaysOf(rows[index]),
        })),
      ),
      impact: sumMoney(estimates.map((estimate) => estimate.monthlyImpact)),
    };
  });
  const totalSaleDays = commonSaleDaysOf(rows) ?? EMPTY_TEXT;
  const totalRoutes = rows.reduce((sum, row) => sum + row.route_count, 0);

  return (
    <TableFooter>
      <TableRow sx={FOOT_ROW_SX}>
        <TableCell sx={[CELL_SX, pinnedSx("BRANCH", FOOTER_CORNER_LAYER), { textAlign: "left" }]}>
          รวม {rows.length} สาขา
        </TableCell>
        <TableCell align="center" sx={[CELL_SX, pinnedSx("DAYS", FOOTER_CORNER_LAYER)]}>
          {totalSaleDays}
        </TableCell>
        <TableCell align="center" sx={[CELL_SX, pinnedSx("ROUTES", FOOTER_CORNER_LAYER)]}>
          {totalRoutes}
        </TableCell>
        {columns.map((column, index) => (
          <TableCell
            key={columnKeyOf(column)}
            align="right"
            sx={[CELL_SX, isGroupStart(columns, index) ? GROUP_SEPARATOR_SX : {}]}
          >
            {formatOrEmpty(columnTotal(column))}
          </TableCell>
        ))}
        {estimateTotals.map(({ side, average, impact }) => (
          <TableCell key={side} align="right" sx={[CELL_SX, side === "SALES" ? GROUP_SEPARATOR_SX : {}]}>
            <Typography component="span" variant="body2" display="block" sx={ESTIMATE_TEXT_SX}>
              {formatOrEmpty(average)}
            </Typography>
            <Typography component="span" variant="caption" color={impact < 0 ? "error.main" : "text.secondary"}>
              ผลต่อเดือน {formatSignedAmount(impact)}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableFooter>
  );
}
